<template>
  <div
    :id="`${namespace}-photoswipe`"
    class="photoswipe-lightbox"
  >
    <template
      v-for="(mediaItem, index) of sortedMedia"
      :key="index"
    >
      <div class="photoswipe-lightbox__media-row">
        <button
          v-if="mediaItem.mediaType === 'video'"
          :ref="`${childRef}-${index}`"
          class="photoswipe-lightbox__trigger"
          :data-index="index"
          :data-pswp-src="formatSource(getVideoSource(mediaItem.sources).url)"
          data-pswp-type="video"
          data-my-size="1920x1080"
          :data-pswp-width="mediaItem.width || 1080"
          :data-pswp-height="mediaItem.height || 1920"
          type="button"
          :disabled="videoTriggerDisabled"
          @click="mouseClick"
        >
          <span
            class="visually-hidden"
            v-text="$string('accessibility.photoswipe.open_video')"
          />

          <video-player
            ref="video"
            class="photoswipe-lightbox__video"
            :class="$class('hidden')"
            :display-overlay="false"
            is-autoplay
            :video="getVideoSource(mediaItem.sources)"
          />

          <video-player
            class="product-carousel__video"
            :display-overlay="false"
            is-autoplay
            :is-active="index === activeIndex"
            :video="getVideoSource(mediaItem.sources)"
            @video-control-hover="handleVideoControlHover"
          />
        </button>

        <button
          v-else
          :ref="`${childRef}-${index}`"
          class="photoswipe-lightbox__trigger"
          :data-index="index"
          :data-pswp-src="formatSource(mediaItem.image?.src) || formatSource(mediaItem.src)"
          :data-pswp-width="mediaItem.image?.width || mediaItem.width"
          :data-pswp-height="mediaItem.image?.height || mediaItem.height"
          type="button"
          @click="mouseClick"
        >
          <span
            class="visually-hidden"
            v-text="$string('accessibility.photoswipe.open_image')"
          />

          <p
            v-if="index === 0 && badgeLabel"
            class="photoswipe-lightbox__product-label"
          >
            {{ badgeLabel }}
          </p>

          <responsive-image
            :loading="index > 0 ? 'lazy' : null"
            :image="mediaItem.image || mediaItem"
            :alt="product.title + ' view ' + (index+1)"
            :ratio="ratio"
          />
        </button>
      </div>
    </template>
    <div
      class="photoswipe-lightbox__pagination"
      :class="{ 'is-opened': isLightboxOpened }"
    >
      <span
        v-for="(item, idx) of sortedMedia"
        :key="`pagination-bar-${idx}`"
        class="photoswipe-lightbox__pagination-bar"
        :class="{
          'is-active': idx === displayActiveIndex,
          'is-opened': isLightboxOpened,
        }"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Photoswipe lightbox (photoswipe-lightbox)
 * -----------------------------------------------------------------------------
 * Component which initialises and renders a series of images which open into a
 * Lightbox from the image's origin.
 * - Modified from: https://photoswipe.com/vue-image-gallery/
 * - Cannot use any element apart from an anchor link to render the lightbox,
 *   so set the role to `presentation` to remove all semantics (mimics Splide).
 *
 * @param {Boolean} [activeIndex] - Optional index that matches with a carousel
 * index to autoplay videos.
 * @param {String} [childRef] - $refs string.
 * @param {Object} [config] - Additional settings for Photoswipe init.
 * @param {Array} media - Array of media items to render in Photobox.
 * @param {String} namespace - Unique namespace.
 * @param {String} title - Modal title.
 *
 */
import PhotoSwipeLightbox from 'photoswipe/lightbox'
import VideoPlayer from '~async/video-player/video-player'
import ResponsiveImage from '~global/images/responsive-image'

export default {
  name: 'PhotoswipeLightbox',

  components: {
    ResponsiveImage,
    VideoPlayer,
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: () => ({
        handle: '',
        images: [],
        media: [],
        title: '',
      }),
    },
    activeIndex: {
      type: [Boolean, Number],
      default: false,
    },
    childRef: {
      type: String,
      default: '',
    },
    config: {
      type: Object,
      default: () => ({}),
    },
    media: {
      type: Array,
      default: () => [],
    },
    namespace: {
      type: String,
      default: '',
      required: true,
    },
  },

  data() {
    return {
      lightbox: null,
      videoTriggerDisabled: false,
      currentActiveIndex: 0,
      cursorPosition: { x: 0, y: 0 },
      isLightboxOpened: false,
      ratio: '7:10',
    }
  },
  computed: {

    /**
   * Returns the badge label if product.tags contains a "badge:" tag.
   * Only used in PDP context.
   */
    badgeLabel() {
      if (!this.product?.tags) {
        return ''
      }
      const badgeTag = this.product.tags.find(tag => tag.startsWith('badge:'))
      if (!badgeTag) {
        return ''
      }
      // Format: badge:Label[:Color]
      const parts = badgeTag.split(':')
      return parts[1] || ''
    },

    /**
     * Always returns the last index of the sortedMedia array
     * This ensures we always show the last media item in pagination
     * regardless of when media loads
     */
    lastMediaIndex() {
      return this.sortedMedia && this.sortedMedia.length > 0
        ? this.sortedMedia.length - 1
        : 0
    },

    /**
     * The active index to use for pagination and other UI elements
     * During lightbox open, use the actual currentActiveIndex
     * Otherwise, always use the last media index
     */
    displayActiveIndex() {
      return this.isLightboxOpened ? this.currentActiveIndex : this.lastMediaIndex
    },

    /**
    * Reorders the media array to ensure videos are at index 1 (second position)
    * @returns {Array} Sorted media array with videos in the second position
    */
    sortedMedia() {
      if (!this.media || this.media.length <= 1) {
        return this.media
      }
      const images = this.media.filter(item => item.mediaType !== 'video')
      const videos = this.media.filter(item => item.mediaType === 'video')
      if (videos.length === 0) {
        return this.media
      }
      const firstImage = images.length > 0 ? [images[0]] : []
      const remainingImages = images.length > 0 ? images.slice(1) : []
      return [...firstImage, ...videos, ...remainingImages]
    },
  },
  watch: {

    /**
     * Focus active media's lightbox trigger button
     * on index change from thumbnail click
     */
    activeIndex(newIndex) {
      const refName = `${this.childRef}-${newIndex}`
      this.$refs[refName][0]?.focus()
    },
  },

  beforeMount() {
    this.localConfig = {
      arrowPrevSVG: '<svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon icon--direction-aware" viewBox="0 0 16 18"><path d="M11 1 3 9l8 8" stroke="currentColor" stroke-linecap="square"></path></svg>',
      arrowNextSVG: '<svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon icon--direction-aware" viewBox="0 0 16 18"><path d="m5 17 8-8-8-8" stroke="currentColor" stroke-linecap="square"></path></svg>',
      closeSVG: '<svg class="icon icon icon__close-latest" viewBox="0 0 16 16" aria-hidden="true"><path d="m1 1 14 14M1 15 15 1" stroke="currentColor"/></svg>',
      zoomSVG: true,
      clickToZoom: true,
      bgOpacity: 1,
      gallery: `#${this.namespace}-photoswipe`,
      children: 'button.photoswipe-lightbox__trigger',
      thumbSelector: 'button',
      initialZoomLevel: 'fit',
      secondaryZoomLevel: 2,
      maxZoomLevel: 4,
      returnFocus: true,
      trapFocus: true,
      loop: true,
      type: 'loop',
      ...this.config,
    }
  },

  mounted() {
    this.lightboxInit()
  },

  unmounted() {
    this.destroyLightbox()
  },

  methods: {
    mouseClick() {
      this.isLightboxOpened = true
      this.hideScrollbar()
    },

    /**
     * Intitialise lightbox using Photoswipe API.
     */
    lightboxInit() {
      if (this.lightbox) {
        return
      }
      this.lightbox = new PhotoSwipeLightbox({
        ...this.localConfig,
        pswpModule: () => import('photoswipe'),
      })
      this.lightboxEventListeners()
      this.lightbox.init()
    },

    /**
     * Hide scrollbar on HTML root element when lightbox is opened
     */
    hideScrollbar() {
      document.documentElement.style.overflow = 'hidden'
    },

    /**
     * Restore scrollbar on HTML root element when lightbox is closed
     */
    showScrollbar() {
      document.documentElement.style.overflow = ''
    },

    /**
     * Function to mouse event listeners for lightbox.
     */
    lightboxEventListeners() {
      this.lightbox.addFilter('itemData', (itemData) => {
        const videoUrl = itemData.element.dataset.pswpSrc

        if (!videoUrl) {
          return false
        }

        itemData.videoUrl = videoUrl
        return itemData
      })

      /**
       * Required to allow `<button>` elements to trigger opening the lightbox
       * with the appropriate image populated.
       */
      this.lightbox.addFilter('domItemData', (itemData) => {
        itemData.src = itemData.element.dataset.pswpSrc
        itemData.h = itemData.element.dataset.pswpHeight
        itemData.w = itemData.element.dataset.pswpWidth

        return itemData
      })

      /**
       * Add necessary ARIA attributes to root element.
       */
      this.lightbox.on('firstUpdate', () => {
        const rootElement = this.lightbox.pswp.element
        rootElement.setAttribute('role', 'dialog')
        rootElement.setAttribute('aria-modal', true)
        rootElement.setAttribute('aria-label', this.$string('accessibility.photoswipe.modal_label'))
      })

      this.lightbox.on('contentLoad', (event) => {
        const { content } = event

        if (this.sortedMedia[content.index].mediaType !== 'video') {
          return
        }

        event.preventDefault()
        content.element = this.buildLightboxVideo()
      })

      this.lightbox.on('change', () => {
        this.currentActiveIndex = this.lightbox.pswp.currIndex
      })

      this.lightbox.on('close', () => {
        this.isLightboxOpened = false
        this.showScrollbar()
      })
    },

    /**
     * Create div to hold video player component in lightbox.
     * @returns {Boolean|HTMlElement}
     */
    buildLightboxVideo() {
      const videoContainer = document.createElement('div')
      videoContainer.className = 'photoswipe-lightbox__video-container'

      const videoCopy = this.$refs.video.map((element) => element)

      if (!videoCopy) {
        return false
      }

      const videoCopyElement = videoCopy[0].$el

      videoCopyElement.classList.remove('is-hidden')
      videoContainer.appendChild(videoCopyElement)

      return videoContainer
    },

    /**
     * Destroy lightbox and remove event listeners.
     */
    destroyLightbox() {
      if (!this.lightbox) {
        return
      }
      this.lightbox.destroy()
      this.lightbox = null
    },

    /**
     * Get video MP4 source object.
     * @param {Array} sources - Video sources.
     * @returns {Object}
     */
    getVideoSource(sources) {
      return sources?.find((source) => source.format === 'mp4')
    },

    /**
     * Format media source URL to ensure it begins with protocol
     * @param {String} url - Media source URL.
     */
    formatSource(url) {
      if (!url) {
        return ''
      }

      return url.match(/^https?/) ? url : `https:${url}`
    },

    /**
     * Handle video control hover events to disable lightbox trigger.
     * @param {Boolean} hovered - Whether the controls were hovered.
     */
    handleVideoControlHover(hovered) {
      this.videoTriggerDisabled = hovered
    },
  },
}
</script>

<style lang="scss">
@import './photoswipe-lightbox';
@import '@/components/photoswipe';
</style>
